/*
 * fn: getCardMapQuery
 *
 * Creates a React Query fn and key to batch request a map of cards.
 *
 * Will either make individual calls under a threshhold, or chunk and send to the batch API endpoint.
 *
 */
import { type QueryClient, type QueryFunction, type QueryKey } from '@tanstack/react-query'

import { type CardIdType, type CardMapType } from '@/defs/card'
import { fetchCard, fetchCardBatch } from '@/utils/apiUtil'
import { objectEntries } from '@/utils/commonUtil'

import { getCardQuery } from './getCardQuery'

type QueryReturnType = {
  queryFn: QueryFunction<CardIdType[], QueryKey>
  queryKey: readonly [string, string]
}

type Props = {
  cardIds: CardIdType[]
  queryClient: QueryClient
}

const API_THRESHOLD = 30
const CHUNK_SIZE = 100

const fetchBatch = async ({ cardIds }: Omit<Props, 'queryClient'>): Promise<CardMapType> => {
  if (cardIds.length < 1) return {}
  const cards: CardMapType = {}

  if (cardIds.length < API_THRESHOLD) {
    const results = await Promise.allSettled(cardIds.map(async (cardId) => [cardId, await fetchCard(cardId)] as const))
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const [cardId, card] = result.value
        cards[cardId] = card
      }
    })
    return cards
  }

  const chunks: CardIdType[][] = []
  for (let i = 0; i < cardIds.length; i += CHUNK_SIZE) {
    chunks.push(cardIds.slice(i, i + CHUNK_SIZE))
  }

  const batches = await Promise.allSettled(chunks.map((cardIds) => fetchCardBatch({ cardIds })))
  batches.forEach((batch) => {
    if (batch.status === 'fulfilled') Object.assign(cards, batch.value)
  })

  return cards
}

const queryFn = async ({ cardIds: cardIdsInput, queryClient }: Props) => {
  const batched = await fetchBatch({ cardIds: cardIdsInput })
  const cardIds: CardIdType[] = []
  objectEntries(batched).forEach(([cardId, card]) => {
    if (!card) return
    queryClient.setQueryData(getCardQuery(cardId).queryKey, card)
    cardIds.push(cardId)
  })
  return cardIds
}

export const getCardMapQuery = (props: Props): QueryReturnType => ({
  queryFn: () => queryFn(props),
  queryKey: ['cardMap', [...props.cardIds].sort().join('==')],
})
